import { useMemo, type ReactNode } from "react";
import {
  Divider,
  Group,
  Loader,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  Button,
} from "@mantine/core";
import { IconAlertTriangle, IconInbox } from "@tabler/icons-react";
import type {
  PathologistReviewQueueCase,
  QueueColumnDef,
} from "./queueColumnConfig";
import type { QueueSortState } from "./pathologistReviewQueueView";

export interface QueuePagination {
  page: number;
  totalPages: number;
  cases: PathologistReviewQueueCase[];
}

interface QueueCaseTableProps {
  cases: PathologistReviewQueueCase[];
  pagination: QueuePagination;
  loading: boolean;
  filtered: boolean;
  blocked?: boolean;
  totalCount?: number;
  columns: QueueColumnDef[];
  sortState: QueueSortState | QueueSortState[] | null;
  onSort: (columnId: string) => void;
  onPageChange: (page: number) => void;
  onClearFilters: () => void;
  renderHeader: (
    columns: QueueColumnDef[],
    sortState: QueueSortState | QueueSortState[] | null,
    onSort: (columnId: string) => void
  ) => ReactNode;
  renderRow: (
    caseItem: PathologistReviewQueueCase,
    blocked: boolean,
    columns: QueueColumnDef[]
  ) => ReactNode;
}

export function QueueCaseTable({
  cases,
  pagination,
  loading,
  filtered,
  blocked = false,
  totalCount,
  columns,
  sortState,
  onSort,
  onPageChange,
  onClearFilters,
  renderHeader,
  renderRow,
}: QueueCaseTableProps) {
  const tableMinWidth = useMemo(
    () => columns.reduce((width, column) => width + (column.width ?? 140), 0),
    [columns]
  );

  return (
    <Paper p="md" withBorder>
      <Stack gap="md">
        {blocked && (
          <>
            <Group gap="sm">
              <IconAlertTriangle size={20} color="var(--mantine-color-red-6)" />
              <Text fw={500} size="lg" c="red">
                Blocked Cases ({totalCount ?? cases.length})
              </Text>
            </Group>
            <Text size="sm" c="dimmed">
              These cases have reported errors and are blocked until the issue
              is resolved.
            </Text>
            <Divider />
          </>
        )}

        {pagination.totalPages > 1 && (
          <Group justify="flex-end">
            <Pagination
              total={pagination.totalPages}
              value={pagination.page}
              onChange={onPageChange}
              size="sm"
            />
          </Group>
        )}

        {loading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : cases.length === 0 ? (
          <Stack align="center" gap="sm" py="xl">
            <IconInbox size={36} color="var(--mantine-color-gray-5)" />
            <Text fw={500} ta="center">
              {filtered
                ? "No cases match the current filters"
                : "No cases currently awaiting pathologist review."}
            </Text>
            {filtered && (
              <Button
                size="compact-sm"
                variant="subtle"
                onClick={onClearFilters}
              >
                Clear Filters
              </Button>
            )}
          </Stack>
        ) : (
          <Table.ScrollContainer minWidth={tableMinWidth} type="native">
            <Table striped highlightOnHover>
              {renderHeader(columns, sortState, onSort)}
              <Table.Tbody>
                {pagination.cases.map((caseItem) =>
                  renderRow(caseItem, blocked, columns)
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Paper>
  );
}
